// Locale resolution and switching.
//
// Split out of i18n.js because these are the only functions that read session
// data. Keeping them here lets i18n.js (and the @/utils/i18n barrel that
// re-exports it) stay a leaf, so modules that only need i18n_t do not inherit
// the session layer.
//
// Precedence for the active locale is: ?locale= override for Pendozers, then the
// user's locale, then the subscription's, then the browser's, then 'en'.
import { normalizeLocale } from '@pendo/i18n';
import { getFileFromCDN } from '@/utils/get-from-cdn.utils';
import i18nDateTimeFormatter from './i18n-datetime-formatter';
import i18nListFormatter from './i18n-list-formatter';
import { getActiveUser, getActiveSubscription } from '@/data/session/session-globals';
import { SUPPORTED_LOCALES, UNANNOUNCED_LOCALES } from '@/../locale.constants.mjs';
import { i18n } from './i18n';
import momentTimeZone from 'moment-timezone';

function getBrowserLocale () {
    const regionalLanguage = window.navigator.language;

    if (!regionalLanguage) {
        return false;
    }

    // Only Pendozers should access unannounced non-English locales
    if (!getActiveUser()?.isPendozer && UNANNOUNCED_LOCALES.includes(regionalLanguage)) {
        return 'en';
    }

    const noVariantLocale = regionalLanguage.split('-')[0];

    // we do not care about regional variants of en, es, ja, or de (for now)
    return ['en', 'es', 'ja', 'de'].includes(noVariantLocale) ? noVariantLocale : regionalLanguage;
}

function getLocaleOverride () {
    if (!getActiveUser()?.isPendozer) {
        return null;
    }

    const params = new URLSearchParams(window.location.search);

    return params.get('locale');
}

function refreshLocale () {
    const locale =
        getLocaleOverride() || getActiveUser()?.locale || getActiveSubscription()?.locale || getBrowserLocale() || 'en';

    return setLocale(locale);
}

async function setLocale (locale) {
    if (!locale || locale === i18n.locale) {
        return Promise.resolve();
    }

    const originalLocale = locale;
    locale = normalizeLocale(locale);

    momentTimeZone.locale(locale);
    i18nDateTimeFormatter.locale = locale;
    i18nListFormatter.locale = locale;

    const loadLocaleFileFn = (loc) => import(`@/locales/${normalizeLocale(loc)}.json`);

    return i18n.changeLocale({ locale: originalLocale, loadLocaleFileFn });
}

async function loadLocaleOptions () {
    // https://pendo-io.atlassian.net/browse/APP-79945
    // Locale file is not fully functional, so we need to manually add supported locales
    const locales = await getFileFromCDN('/i18n/locales.json');

    // Make sure all locales in SUPPORTED_LOCALES are included
    SUPPORTED_LOCALES.forEach((supportedLocale) => {
        if (!locales.some((locale) => locale.id === supportedLocale)) {
            switch (supportedLocale) {
                case 'en':
                    locales.push({ id: supportedLocale, displayName: 'English' });
                    break;
                case 'ja':
                    locales.push({ id: supportedLocale, displayName: '日本語' });
                    break;
                case 'de':
                    locales.push({ id: supportedLocale, displayName: 'Deutsch' });
                    break;
                case 'pt-BR':
                    locales.push({ id: supportedLocale, displayName: 'Português' });
                    break;
                case 'es':
                    locales.push({ id: supportedLocale, displayName: 'Español' });
                    break;
            }
        }
    });

    const localeOptions = locales.filter((locale) => SUPPORTED_LOCALES.includes(locale.id));

    // This is temporary until we officially support/announce these locales
    if (getActiveUser()?.isPendozer) {
        const unannouncedLocaleDisplayNames = {
            'fr-FR': 'Français',
        };

        UNANNOUNCED_LOCALES.forEach((localeId) => {
            localeOptions.push({
                id: localeId,
                displayName: unannouncedLocaleDisplayNames[localeId],
            });
        });
    }

    return localeOptions;
}

export { setLocale, refreshLocale, loadLocaleOptions };
